import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Formik, Form } from 'formik';
import { Link } from 'react-router-dom';
import { LinkButton } from 'cstar-react-primitives';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { useFormatMessage } from '../../../intl/intlHooks';
import { useSegment } from '../../../segment/SegmentProvider';
import { addOrUpdateLineItem, removeLineItem } from '../../../lineItem/lineItemModule';

import { cashbotName } from '../../core';
import { Dialog } from '../../dialog/Dialog';
import { getProductEditPath } from '../../paths/utils';
import { QuantityPicker } from '../fields/QuantityPicker';
import { FaceplateImg } from '../../faceplates/FaceplateImg';
import { getCardFee } from '../../../lineItem/lineItemSelectors';

import { listingFormMessages } from './lineItemMessages';
import { LineItemTextDetails } from './LineItemTextDetails';
import { useApplyPromos } from '../../../mapi/ApplyPromosProvider';

import ShippingOrRecipientMessage from './ShippingOrRecipientMessage'
import DetailsLinkButton from './DetailsLinkButton'
import PromoName from './PromoName'
import NonQualifiedDiscount from './NonQualifiedDiscount'
import DiscountAmount from './DiscountAmount'

export const InnerLineItemListingForm = ({ values, lineItem, saveLineItem, deleteLineItem }) => {
    const { name, id } = lineItem;
    const { getPromotionalCostForLineItem, promoLoading } = useApplyPromos();
    const formatMessage = useFormatMessage();
    const analytics = useSegment();
    const productFaceplate = values.product.faceplates.find(fp => fp.code === values.faceplateCode);
    const defaultLineItemCost = lineItem.quantity * Number(lineItem.amount); 
    const { lineItemPromoCost = defaultLineItemCost, discountRewardAmount = 0.00 } = getPromotionalCostForLineItem(id) || {};
    const singleCardFee = getCardFee(lineItem);
    const totalCost = lineItemPromoCost + (lineItem.quantity * singleCardFee);
    
    const removeButtonRef = useRef(null);
    const showButton = values.deliveryMethod === 'DIGITAL' ? !values.isSelfBuy : Boolean(values.message);
    const [detailsVisible, setDetailsVisible] = useState(false);
    const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
    const lineItemPromoName = lineItem.product.promo && lineItem.product.promo.promoDisplayShortDesc || '';


    const removeItemHandler = (item) => {
        deleteLineItem(item);
        const {product, faceplate, amount, quantity} = item;
        analytics.productRemoved({product, faceplate, amount, quantity});
    };

    useEffect(() => {
        saveLineItem(values);
    }, [values.quantity]);

    const removeButtonProps = {
        type: 'button',
        id: values.id,
        onClick: () => setIsRemoveDialogOpen(true),
        isCentered: false,
        atts: { tabIndex: isRemoveDialogOpen ? -1 : undefined, ref: removeButtonRef, role: 'button' },
        cashbotName
    };

    const linkButtonProps = {
        itemId: values.id,
        detailsVisible,
        setDetailsVisible
    };


    return (
        <div className="line-item">
            <Form>
                {isRemoveDialogOpen && (
                    <Dialog
                        prompt={formatMessage(listingFormMessages.removeLineItemPrompt)}
                        primaryText={formatMessage(listingFormMessages.removeLineItemConfirm)}
                        primaryAction={() => removeItemHandler(values)}
                        secondaryText={formatMessage(listingFormMessages.removeLineItemCancel)}
                        secondaryAction={() => {
                            setIsRemoveDialogOpen(false);
                            removeButtonRef.current.focus();
                        }}
                    />
                )}
                <ShippingOrRecipientMessage lineItem={values} />
                <div className="line-item__inner">
                    <h4 className="line-item-name">{name}</h4>
                    {lineItemPromoName && (<PromoName lineItemPromoName={lineItemPromoName} />)}
                    {lineItemPromoName && !discountRewardAmount && !promoLoading && (<NonQualifiedDiscount />)}
                    <div className="line-item-contents">
                        <div className="line-item-image">
                            <FaceplateImg faceplate={productFaceplate} />
                        </div>
                        <div className="line-item-details">
                            <div className="line-item-amount">
                                <FormattedNumber value={values.amount} format="currency" />
                            </div>
                            {!!singleCardFee && (
                            <div className="line-item-fee">
                                <span>
                                    {formatMessage(listingFormMessages.cardFeeText)}
                                    &nbsp;
                                    <FormattedNumber value={singleCardFee} format="currency" />
                                </span>
                            </div>
                            )}
                            {!!discountRewardAmount && (<DiscountAmount discountAmount={discountRewardAmount} />)}
                            <QuantityPicker />
                        </div>
                    </div>

                    <div className="line-item-bottom">
                        <div className="line-item-controls">
                            <LinkButton {...removeButtonProps}>
                                <FormattedMessage {...listingFormMessages.removeButtonText} />
                            </LinkButton>
                            <Link to={getProductEditPath(values)} tabIndex={isRemoveDialogOpen ? -1 : undefined}>
                                <FormattedMessage {...listingFormMessages.editButtonText} />
                            </Link>
                            {showButton && <DetailsLinkButton {...linkButtonProps} />}
                        </div>

                        <div className="line-item-total">
                            <FormattedNumber value={totalCost} format="currency" />
                        </div>
                        {detailsVisible && <LineItemTextDetails {...lineItem} />}
                    </div>
                </div>
            </Form>
        </div>
    );
};

InnerLineItemListingForm.propTypes = {
    values: PropTypes.shape({
        message: PropTypes.string,
        id: PropTypes.string.isRequired,
        amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        isSelfBuy: PropTypes.bool.isRequired,
        deliveryMethod: PropTypes.string.isRequired,
        quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        sg: PropTypes.object,
        product: PropTypes.object,
        faceplateCode: PropTypes.string
    }).isRequired,
    lineItem: PropTypes.object.isRequired,
    saveLineItem: PropTypes.func.isRequired,
    deleteLineItem: PropTypes.func.isRequired
};

const mapDispatchToProps = dispatch => ({
    saveLineItem: lineItem => dispatch(addOrUpdateLineItem(lineItem)),
    deleteLineItem: lineItem => dispatch(removeLineItem(lineItem))
});

export const LineItemListingForm = connect(undefined, mapDispatchToProps)(InnerLineItemListingForm);

export const LineItemListingFormik = ({ lineItem }) => (
    <Formik
        onSubmit={() => {}}
        initialValues={lineItem}
        children={formProps => <LineItemListingForm {...formProps} lineItem={lineItem} />}
    />
);

LineItemListingFormik.propTypes = {
    lineItem: PropTypes.object.isRequired
};
